import React, { FC, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';

import { SHARE_EXCHANGE_STAGES } from 'constants/deals';
import { COMPLETE_SHARE_EXCHANGE_MODAL_FIELDS } from 'constants/share-exchange';
import { errorNotify } from 'helpers';
import { UseModalReturnValues } from 'hooks/use-modal/useModal';
import { Deal } from 'interfaces';
import { completeDealsParametersById } from 'modules/deals/action';
import { useAppDispatch } from 'modules/store';
import { AlertModal, FormContent } from 'shared-components';
import Typography from 'shared-components/Typography';
import { removeLabelFromFormFields } from 'utils/shared';

interface Props extends Omit<UseModalReturnValues, 'onOpen'> {
  selectedDeals: Deal[];
  selectedDealsIds: number[];
  resetSelectedDeals?: VoidFunction;
  isActionLoading?: boolean;
  closingDate: string | null;
}

const CompleteShareExchangesModal: FC<Props> = ({
  selectedDeals,
  selectedDealsIds,
  resetSelectedDeals,
  isActionLoading,
  closingDate,
  ...modalProps
}) => {
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    formState: { errors },
    control,
    setValue,
    handleSubmit,
  } = useForm<{ officialClosingDate: string | null }>({
    defaultValues: { officialClosingDate: closingDate },
  });

  useEffect(() => {
    if (closingDate) {
      setValue('officialClosingDate', closingDate, { shouldDirty: false });
    }
  }, [closingDate, setValue]);

  const handleCompleteDealsParameters = ({ officialClosingDate }: { officialClosingDate: string | null }) => {
    const isNotReadyToComplete = selectedDeals.some(
      (shareExchange) => shareExchange?.stage !== SHARE_EXCHANGE_STAGES.READY_TO_COMPLETE,
    );
    if (isNotReadyToComplete) {
      errorNotify(`Exchanges not in the 'Ready to Complete' stage cannot be completed.`);
      return;
    }
    setIsLoading(true);

    dispatch(completeDealsParametersById({ dealIds: selectedDealsIds, officialClosingDate: officialClosingDate || '' }))
      .unwrap()
      .then(() => {
        resetSelectedDeals && resetSelectedDeals();
      })
      .finally(() => {
        setIsLoading(false);
        modalProps.onClose();
      });
  };

  return (
    <AlertModal
      onSubmit={handleSubmit(handleCompleteDealsParameters)}
      isLoading={isActionLoading || isLoading}
      title={'Please enter the official closing date'}
      submitButtonText={'Confirm'}
      modalClassName='lg:w-9/12 xl:w-7/12 overflow-visible'
      contentClassName='lg:px-28 lg:py-12'
      wrapperModalClassName='lg:py-0 lg:px-0'
      {...modalProps}
    >
      <div className='flex flex-row w-full items-end flex-wrap pt-10 pb-2'>
        <Typography className='max-w-[500px] mb-4 mr-4 text-black text-[16px]'>
          Add date and click confirm to complete the share exchanges
        </Typography>
        <form className='w-[180px] mb-0'>
          <FormContent
            anchorPrefix={'complete-share-exchanges'}
            control={control}
            fields={removeLabelFromFormFields(COMPLETE_SHARE_EXCHANGE_MODAL_FIELDS)}
            errors={errors}
            register={register}
          />
        </form>
      </div>
    </AlertModal>
  );
};

export default CompleteShareExchangesModal;
